import { type Brand, make } from "ts-brand";
import { z } from "zod";

import { MAX_BULK_DOWNLOAD_FILE_COUNT } from "./download-constraints";
import { type MandateCategory, MandateCategoryId, MandateId, PersonId } from "./mandate";
import { PageRequestSchema } from "./pagination";
import {
    ALLOWED_UPLOAD_TYPES_LABEL,
    base64ByteLength,
    checkUploadBatch,
    checkUploadFile,
    MAX_UPLOAD_BATCH_SIZE_LABEL,
    MAX_UPLOAD_FILE_COUNT,
    MAX_UPLOAD_FILE_SIZE_LABEL,
} from "./upload-constraints";

/**
 * Document domain model (Core 1 — Mandate Workspace). A Document is any file in
 * a Mandate's shared file space — the blob lives in the `TrexpertBlobConnector`
 * (S3/MinIO), the metadata here. `sender` records who sent it: `"TRUSTEE"` is a
 * Trexpert clerk *providing* a file to the customer (S1), `"CUSTOMER"` is a
 * customer *submitting* a file to Trexpert (S3, formerly modelled as a separate
 * "Receipt"). Both share one lifecycle (`provided → retrieved`) and one list.
 * See docs/architecture.md § "What PostgreSQL holds". Nominal IDs via ts-brand;
 * Zod validates at the boundary.
 */

export type DocumentId = Brand<string, "DocumentId">;
export const DocumentId = make<DocumentId>();

/**
 * Standard categories from the oracle's `kategorien.js`. A Category groups a
 * Mandate's documents; per-mandate catalogs are managed at runtime.
 */
export const STANDARD_CATEGORIES = [
    "lohnabrechnungen",
    "lohnausweise",
    "mwst",
    "jahresrechnungen",
    "qst",
    "steuererklaerungen",
    "dauerakten",
    "beleg",
] as const;

/**
 * Category is now a plain string — per-mandate catalogs are managed at runtime.
 * `STANDARD_CATEGORIES` remain the seeded defaults for new Mandates.
 */
export type Category = string;

/**
 * The category customers reach for by default: they overwhelmingly upload
 * Belege, so the customer upload form pre-selects «Beleg» (decision from the
 * 2026-07-08 feedback round). The trustee default is unaffected.
 */
export const BELEG_CATEGORY: Category = "beleg";

/**
 * The fallback «Sammelkategorie» a Document is moved to when its category is
 * deleted from the Mandate catalog (FB-M3b): the document stays findable under
 * «Ohne Kategorie» instead of pointing at a vanished id. Reserved — never offered
 * as a deletable catalog entry.
 */
export const UNCATEGORIZED_CATEGORY: Category = "ohne-kategorie";

/**
 * Categories that carry a fixed label and deliberately have **no** catalog row:
 * a Mandate never seeds them and the catalog editor never offers them, so they
 * cannot be renamed or removed. Every surface that resolves a label through the
 * Mandate's catalog consults this first, otherwise a reserved id reads as a raw
 * slug and the filter marks it «(entfernt)» — reserved is not removed.
 */
export const RESERVED_CATEGORY_LABELS: Readonly<Record<string, string>> = {
    [UNCATEGORIZED_CATEGORY]: "Ohne Kategorie",
};

/** German UI labels for the standard categories. Used for seeding and display fallback. */
export const CATEGORY_LABELS: Readonly<Record<string, string>> = {
    lohnabrechnungen: "Lohnabrechnungen",
    lohnausweise: "Lohnausweise",
    mwst: "MWST",
    jahresrechnungen: "Jahresrechnungen",
    qst: "Quellensteuer",
    steuererklaerungen: "Steuererklärungen",
    dauerakten: "Dauerakten",
    beleg: "Beleg",
    [UNCATEGORIZED_CATEGORY]: "Ohne Kategorie",
};

/**
 * The category catalog a freshly created Mandate is seeded with — the standard
 * categories paired with their German labels. The *defaults* live here beside
 * the category vocabulary; `applyCreateMandate` owns the creation *rules*.
 */
export const defaultMandateCategories = (): MandateCategory[] =>
    STANDARD_CATEGORIES.map(id => ({
        id: MandateCategoryId(id),
        label: CATEGORY_LABELS[id] ?? id,
    }));

/** Map a German label (or a slug) back to its slug among the standard categories, if known. */
export const categoryFromLabel = (value: string): Category | undefined => {
    const normalized = value.trim().toLowerCase();
    const bySlug = STANDARD_CATEGORIES.find(category => category === normalized);
    if (bySlug) {
        return bySlug;
    }
    return STANDARD_CATEGORIES.find(category => CATEGORY_LABELS[category]?.toLowerCase() === normalized);
};

/** `"TRUSTEE"` = Trexpert → customer (provided, S1); `"CUSTOMER"` = customer → Trexpert (submitted, S3). */
export const SENDERS = ["TRUSTEE", "CUSTOMER"] as const;
export type Sender = (typeof SENDERS)[number];

/**
 * Lifecycle of a Document. `in_review` once a clerk uploads it as a draft
 * (four-eyes review — invisible to the customer); `provided` once a *second*
 * clerk accepts it, or directly on a non-draft upload; `retrieved` once the
 * *counterparty* downloads it (a customer for a trustee-provided file, a trustee
 * for a customer-submitted one — never a same-side download, see
 * {@link isCounterpartyRetrieval}). All transitions are one-way and idempotent
 * (decisions D3 / D-DR-1, gate P2-G5). See {@link accept}, {@link markRetrieved}.
 */
export const DOCUMENT_STATUSES = ["in_review", "provided", "retrieved"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const CategorySchema = z.string().min(1);
export const SenderSchema = z.enum(SENDERS);
export const DocumentStatusSchema = z.enum(DOCUMENT_STATUSES);

/**
 * The Document as shown in a Mandate workspace (the client view). The blob key
 * is server-internal and never crosses the boundary — the `retrieveDocument`
 * function returns the file bytes (base64) directly instead (D2 reversed).
 */
export const DocumentSchema = z.object({
    id: z.string().min(1).transform(DocumentId),
    mandateId: z.string().min(1).transform(MandateId),
    category: CategorySchema,
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    /**
     * The customer's free-text remark (Bemerkung) that rides with the Document —
     * e.g. «mit Privatkonto bezahlt». Set when the client submits a Beleg with a
     * remark, on the free upload and the position upload path alike. The portal is
     * the source of truth; the trustee notification mail carries a snapshot.
     */
    remark: z.string().min(1).optional(),
    sender: SenderSchema,
    status: DocumentStatusSchema,
    providedAt: z.string().min(1),
    providedBy: z.string().min(1).transform(PersonId),
    /** Who uploaded the draft for review — set on a draft upload (four-eyes audit). */
    reviewedBy: z.string().min(1).transform(PersonId).optional(),
    /** When the draft was accepted (the `in_review → provided` transition). */
    acceptedAt: z.string().min(1).optional(),
    /** Who accepted the draft — must differ from `reviewedBy` (four-eyes invariant). */
    acceptedBy: z.string().min(1).transform(PersonId).optional(),
    retrievedAt: z.string().min(1).optional(),
    /** Who first retrieved the Document — set once, on the `provided → retrieved` transition (FADP audit). */
    firstRetrievedBy: z.string().min(1).transform(PersonId).optional(),
    /**
     * Trustee management flag — orthogonal to the download lifecycle.
     * Archived documents are hidden from the main list but accessible via
     * `listArchivedDocuments`. Defaults to false.
     */
    archived: z.boolean().default(false),
    archivedAt: z.string().min(1).optional(),
    archivedBy: z.string().min(1).transform(PersonId).optional(),
});

export type Document = z.infer<typeof DocumentSchema>;

/**
 * The persisted Document record: the client view plus the server-only blob key
 * (the object-storage key the `TrexpertBlobConnector` reads/writes).
 */
export interface DocumentRecord extends Document {
    blobKey: string;
}

/** Strip the server-only fields to produce the client-facing {@link Document}. */
export const toDocument = (record: DocumentRecord): Document => ({
    id: record.id,
    mandateId: record.mandateId,
    category: record.category,
    fileName: record.fileName,
    contentType: record.contentType,
    sizeBytes: record.sizeBytes,
    remark: record.remark,
    sender: record.sender,
    status: record.status,
    providedAt: record.providedAt,
    providedBy: record.providedBy,
    reviewedBy: record.reviewedBy,
    acceptedAt: record.acceptedAt,
    acceptedBy: record.acceptedBy,
    retrievedAt: record.retrievedAt,
    firstRetrievedBy: record.firstRetrievedBy,
    archived: record.archived,
    archivedAt: record.archivedAt,
    archivedBy: record.archivedBy,
});

/** The fields a caller supplies to provide (upload) a Document. */
export interface ProvideDocumentInput {
    id: DocumentId;
    mandateId: MandateId;
    category: Category;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    blobKey: string;
    sender: Sender;
    providedBy: PersonId;
    providedAt: string;
    /** The customer's remark (Bemerkung) that rides with the Document, if given. */
    remark?: string;
    /** Trustee draft request — honoured only for a `"TRUSTEE"` sender. */
    draft?: boolean;
}

/**
 * Assemble a {@link DocumentRecord} for a newly uploaded file. Owns the
 * initial-lifecycle rule: a draft is a trustee-only four-eyes flow (D-DR-1), so
 * the record starts `in_review` (and captures `reviewedBy`) only when a
 * `"TRUSTEE"` sender requested a draft; every other upload publishes directly as
 * `provided`. A customer upload can never be a draft. The caller owns IO concerns
 * (id, blob key, decoded `sizeBytes`); the rules live here. Read `status` back to
 * learn whether the result is a draft rather than recomputing the predicate.
 */
export const provideDocument = (input: ProvideDocumentInput): DocumentRecord => {
    const isDraft = input.sender === "TRUSTEE" && (input.draft ?? false);
    return {
        id: input.id,
        mandateId: input.mandateId,
        category: input.category,
        fileName: input.fileName,
        contentType: input.contentType,
        sizeBytes: input.sizeBytes,
        sender: input.sender,
        status: isDraft ? "in_review" : "provided",
        providedAt: input.providedAt,
        providedBy: input.providedBy,
        ...(input.remark ? { remark: input.remark } : {}),
        ...(isDraft && { reviewedBy: input.providedBy }),
        blobKey: input.blobKey,
        archived: false,
    };
};

/**
 * Raised when `accept` is called on a Document that is not `in_review`. Only a
 * review draft can be accepted (decision D-DR-1).
 */
export class InvalidDocumentTransitionError extends Error {
    readonly code = "invalid_document_transition";

    constructor(from: DocumentStatus, to: DocumentStatus) {
        super(`invalid_document_transition: ${from} → ${to}`);
        this.name = "InvalidDocumentTransitionError";
    }
}

/**
 * The one-way `in_review → provided` transition (decision D-DR-1). Any trustee
 * holding `provide-document` may release a review draft — including the person
 * who provided it (self-release). The former four-eyes ban (D-DR-2: the acceptor
 * had to differ from the uploader) was lifted on 2026-07-23 (Michael; Kundenwunsch
 * «jeder darf freigeben»). Throws {@link InvalidDocumentTransitionError} when the
 * Document is not in review. Captures `acceptedBy` / `acceptedAt` for the release
 * audit (who released — including a self-release — is still recorded).
 */
export const accept = <
    T extends {
        status: DocumentStatus;
        providedBy: PersonId;
        reviewedBy?: PersonId;
        acceptedAt?: string;
        acceptedBy?: PersonId;
    },
>(
    document: T,
    acceptedAt: string,
    acceptedBy: PersonId
): T => {
    if (document.status !== "in_review") {
        throw new InvalidDocumentTransitionError(document.status, "provided");
    }
    return { ...document, status: "provided", acceptedAt, acceptedBy };
};

/**
 * The one-way `provided → retrieved` transition (decision D3, gate P2-G5).
 * Idempotent: retrieving an already-retrieved Document keeps its original
 * `retrievedAt` and `firstRetrievedBy`. There is no transition back to
 * `provided`. The first retriever is captured once, on the first transition,
 * for the FADP first-retrieval audit (dokument-austausch sc.3).
 */
export const markRetrieved = <
    T extends { status: DocumentStatus; retrievedAt?: string; firstRetrievedBy?: PersonId },
>(
    document: T,
    retrievedAt: string,
    retrievedBy: PersonId
): T => {
    if (document.status === "retrieved") {
        return document;
    }
    return { ...document, status: "retrieved", retrievedAt, firstRetrievedBy: retrievedBy };
};

/**
 * Whether a download by `retrieverSide` is a hand-over to the *counterparty* and
 * therefore moves the Document `provided → retrieved`. The lifecycle tracks
 * whether the *other* party has seen the file: a trustee downloading a
 * trustee-provided document, or a customer re-downloading their own submission,
 * is an own-side read and must NOT flip the status — only a download from the
 * side opposite the `sender` counts. `retrieverSide` is the downloader's side
 * derived the same way as a Document's `sender` on upload (a clerk holding
 * `provide-document` ⇒ `"TRUSTEE"`, otherwise `"CUSTOMER"`).
 */
export const isCounterpartyRetrieval = (sender: Sender, retrieverSide: Sender): boolean =>
    sender !== retrieverSide;

/**
 * Whether a `provided` Document reads as «neu» / ungelesen for `viewerSide`. Only
 * counterparty uploads count — the uploader's own side never sees its file as new
 * (oracle: MandatDetail flags «neu» only for `richtung === 'eingehend'`; KundeDokumente
 * only for `richtung === 'ausgehend'`). Uses the same sender-vs-viewer rule as
 * {@link isCounterpartyRetrieval}.
 */
export const isUnreadForViewer = (status: DocumentStatus, sender: Sender, viewerSide: Sender): boolean =>
    status === "provided" && isCounterpartyRetrieval(sender, viewerSide);

/**
 * The guarded `provided → retrieved` transition: advance the status only when
 * the download is a {@link isCounterpartyRetrieval | counterparty retrieval},
 * otherwise return the document unchanged. This is the single home for "does
 * this download count as a hand-over?" — both the direct download path
 * (`retrieveDocument`) and the inbox-processing path (`processInboxEntry`) call
 * it, so the two can never drift apart on whether a same-side read flips the
 * status. Idempotent via {@link markRetrieved}.
 */
export const retrieveByCounterparty = <
    T extends { sender: Sender; status: DocumentStatus; retrievedAt?: string; firstRetrievedBy?: PersonId },
>(
    document: T,
    retrieverSide: Sender,
    retrievedAt: string,
    retrievedBy: PersonId
): T =>
    isCounterpartyRetrieval(document.sender, retrieverSide)
        ? markRetrieved(document, retrievedAt, retrievedBy)
        : document;

/**
 * Customer visibility gate (D-DR-1): a Document still `in_review` is a
 * trustee-internal four-eyes draft and must be invisible to the customer
 * everywhere. The rule lives here rather than as an inline status check in the
 * listing service.
 */
export const isVisibleToCustomer = (doc: Document): boolean => doc.status !== "in_review";

/**
 * Set `archived = true` on a Document record (trustee management action).
 * Orthogonal to the download lifecycle — `retrievedAt` and status are unchanged.
 */
export const archiveDocument = <T extends Document>(doc: T, archivedAt: string, archivedBy: PersonId): T => ({
    ...doc,
    archived: true,
    archivedAt,
    archivedBy,
});

/** Restore an archived Document back to the active list. */
export const unarchiveDocument = <T extends Document>(doc: T): T => ({
    ...doc,
    archived: false,
    archivedAt: undefined,
    archivedBy: undefined,
});

/** Re-home a Document into a new Category (trustee recategorization). */
export const applyChangeCategory = <T extends { category: Category }>(doc: T, category: Category): T => ({
    ...doc,
    category,
});

/**
 * The filters the overview's category options are computed under — the list's
 * own filters, minus the category. Offering options from an unfiltered set lets
 * a user pick a category the current selection has no row for: a dead end the
 * label grouping was meant to remove.
 */
export const DocumentOverviewCategoriesInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId).optional(),
    sender: SenderSchema.optional(),
    status: DocumentStatusSchema.optional(),
    archived: z.boolean().default(false),
    search: z.string().optional(),
});
export type DocumentOverviewCategoriesInput = z.infer<typeof DocumentOverviewCategoriesInputSchema>;

/**
 * One option of the cross-mandate overview's category filter. Keyed by the
 * *label*, not the id: the same label can stand for different ids in different
 * Mandates (one Mandate adds «Versicherungen», another renames `dauerakten` to
 * it), and a label-keyed list answers that with one entry that finds both. The
 * reverse — the same id spelled differently — correctly yields two entries.
 */
export interface DocumentOverviewCategoryOption {
    /** The label as shown and as sent back when filtering. */
    value: string;
    /** True when no Mandate carries this category any more — the value is a raw id. */
    orphaned: boolean;
}

/**
 * A single row in the cross-mandate document overview (trustee-only view).
 * Joins one Document with the `companyName` from its parent Mandate so the
 * overview table can show the Mandate column without a per-row lookup.
 */
export interface TrexpertDocumentOverviewItem {
    id: DocumentId;
    mandateId: MandateId;
    companyName: string;
    fileName: string;
    category: Category;
    /**
     * The category's label as the owning Mandate currently spells it — resolved
     * server-side, because a cross-mandate row cannot be labelled from a single
     * catalog and the static standard labels are only a Mandate's seed. Falls back
     * to the raw `category` id when the Mandate no longer carries that category.
     */
    categoryLabel: string;
    sender: Sender;
    status: FileStatus;
    providedAt: string;
    archived: boolean;
    /**
     * Byte size — carried so the overview can pre-flight a bulk ZIP download
     * against {@link checkBulkDownload}, the same bound the Mandatsraum applies.
     * Without it the cross-mandate Sammelaktion would only learn of an oversized
     * selection after the server refused the finished archive.
     */
    sizeBytes: number;
    /** The Beleg-Bemerkung that rides with the Document, shown under its name. */
    remark?: string;
}

/** Alias kept in sync with {@link DocumentStatus} — exposed for the overview item. */
export type FileStatus = DocumentStatus;

/** Sortable columns of the cross-mandate document overview. */
export const DOCUMENT_OVERVIEW_SORT_FIELDS = [
    "companyName",
    "fileName",
    "category",
    "sender",
    "providedAt",
] as const;
export type DocumentOverviewSortField = (typeof DOCUMENT_OVERVIEW_SORT_FIELDS)[number];

/**
 * Boundary query for the paginated cross-mandate document overview (replaces the
 * old `listAllDocuments` fetch-everything). The `search` (from {@link
 * PageRequestSchema}) matches the file name *and* the Mandate company name; the
 * server resolves the company-name half. `sortBy` is constrained to document
 * columns — company-name sort resolves Mandate names in the service layer. With
 * no sort the overview defaults to newest-first.
 */
export const DocumentOverviewQuerySchema = PageRequestSchema.extend({
    sortBy: z.enum(DOCUMENT_OVERVIEW_SORT_FIELDS).optional(),
    /** Restrict the cross-mandate overview to a single Mandate. */
    mandateId: z.string().min(1).transform(MandateId).optional(),
    category: CategorySchema.optional(),
    sender: SenderSchema.optional(),
    /** Lifecycle-status filter (in_review / provided / retrieved) — same axis as
        the Mandatsraum document list, so both toolbars match. */
    status: DocumentStatusSchema.optional(),
    /** Aktiv/Archiv view filter — same axis as the Mandatsraum «Aktive
        Dokumente»/«Archiv» pills. `false` (the default) lists non-archived
        documents; `true` lists the archived ones, cross-mandate. */
    archived: z.boolean().optional(),
});
export type DocumentOverviewQuery = z.infer<typeof DocumentOverviewQuerySchema>;

/**
 * Boundary query for one Mandate's paginated Document list (the workspace
 * «Dateien»/«Archiv» panels — replaces the old `listDocuments(mandateId)`
 * fetch-everything). `category`/`sender` are the panel's filters on both tabs.
 * `search` matches the document file name (substring). With no sort the list
 * defaults to newest-first (`providedAt` desc).
 */
export const MANDATE_DOCUMENTS_SORT_FIELDS = [
    "fileName",
    "category",
    "sender",
    "status",
    "providedAt",
] as const;
export type MandateDocumentsSortField = (typeof MANDATE_DOCUMENTS_SORT_FIELDS)[number];

export const MandateDocumentsQuerySchema = PageRequestSchema.extend({
    mandateId: z.string().min(1).transform(MandateId),
    sortBy: z.enum(MANDATE_DOCUMENTS_SORT_FIELDS).optional(),
    category: CategorySchema.optional(),
    sender: SenderSchema.optional(),
    /** Lifecycle-status filter for the active list (in_review / provided / retrieved). */
    status: DocumentStatusSchema.optional(),
});
export type MandateDocumentsQuery = z.infer<typeof MandateDocumentsQuerySchema>;

/** Input for bulk management actions that operate on a list of Documents. */
export const ManageDocumentsInputSchema = z.object({
    documentIds: z.array(z.string().min(1).transform(DocumentId)).min(1),
});
export type ManageDocumentsInput = z.infer<typeof ManageDocumentsInputSchema>;

/**
 * Input for the bulk ZIP download. Like {@link ManageDocumentsInputSchema} but with
 * the count bound the buffered archive requires — the whole ZIP is held in memory,
 * so the selection must be capped (archive/unarchive are metadata-only and stay
 * unbounded, hence a separate schema). The total-byte cap cannot live here — it
 * needs the per-Document `sizeBytes` from the store — so it is enforced in the
 * service function; see docs/plans/2026-07-29-bulk-download-bounds.md.
 */
export const DownloadDocumentsInputSchema = z.object({
    documentIds: z
        .array(z.string().min(1).transform(DocumentId))
        .min(1)
        .max(MAX_BULK_DOWNLOAD_FILE_COUNT, {
            message: `Pro Download sind höchstens ${MAX_BULK_DOWNLOAD_FILE_COUNT} Dokumente erlaubt.`,
        }),
});
export type DownloadDocumentsInput = z.infer<typeof DownloadDocumentsInputSchema>;

/** Input for bulk category-change (one target category for all selected Documents). */
export const ChangeDocumentCategoryInputSchema = z.object({
    documentIds: z.array(z.string().min(1).transform(DocumentId)).min(1),
    category: CategorySchema,
});
export type ChangeDocumentCategoryInput = z.infer<typeof ChangeDocumentCategoryInputSchema>;

/**
 * Boundary input for `uploadDocument` (the single upload Magellan call). The
 * file bytes travel base64-encoded (decision D2); the server caps the size,
 * derives `sizeBytes`/blob key, and decides the `direction` from the caller's
 * role (a clerk → outbound, a customer → inbound). Validated in `shared` so
 * client and server agree. The task-item binding extension lives in
 * `task-list.ts` ({@link UploadDocumentWithTaskItemInputSchema}) to keep the
 * import direction acyclic.
 */
/**
 * Bare object shape for the single `uploadDocument` call, without the file
 * type/size refinement. Exported so the task-item-binding extension in
 * `task-list.ts` can `.extend()` it (a `.superRefine`-wrapped schema is a
 * `ZodEffects` and cannot be extended) and then re-apply {@link refineUploadFile}.
 */
export const UploadDocumentInputObjectSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    category: CategorySchema,
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    contentBase64: z.string().min(1),
    /** Customer remark (Bemerkung) that rides with the uploaded Document. */
    remark: z.string().min(1).optional(),
    /**
     * Draft mode (four-eyes review, decision D-DR-1). When `true` and the sender
     * is a trustee, the Document is created `in_review`: the customer is NOT
     * notified and gets no inbox entry; instead an entry lands in the trustee's
     * own org signalling "Dokument in Review". A second trustee then accepts it
     * via `acceptDocument`, which runs the deferred customer publish. Ignored for
     * customer (inbound) uploads. Defaults to false (immediate publish).
     */
    draft: z.boolean().optional().default(false),
});

/**
 * `superRefine` body enforcing the upload type allow-list and per-file size cap
 * (PDF/JPEG/PNG/HEIC, 25 MB) on a single inline file. Shared by the single-file
 * {@link UploadDocumentInputSchema} and its task-item extension so every upload
 * path rejects an oversized or wrong-type file identically — the multi-file
 * {@link UploadDocumentsInputSchema} runs the same {@link checkUploadFile} per file.
 */
export const refineUploadFile = (
    value: { fileName: string; contentType: string; contentBase64: string },
    ctx: z.RefinementCtx
): void => {
    const rejection = checkUploadFile({
        fileName: value.fileName,
        contentType: value.contentType,
        sizeBytes: base64ByteLength(value.contentBase64),
    });
    if (rejection) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["contentBase64"],
            message:
                rejection.reason === "size"
                    ? `Datei «${value.fileName}» überschreitet die maximale Dateigrösse von ${MAX_UPLOAD_FILE_SIZE_LABEL}.`
                    : `Datei «${value.fileName}» hat einen nicht erlaubten Typ. Erlaubt sind ${ALLOWED_UPLOAD_TYPES_LABEL}.`,
        });
    }
};

/**
 * Boundary input for the single `uploadDocument` Magellan call — the object
 * shape plus the {@link refineUploadFile} type/size guard.
 */
export const UploadDocumentInputSchema = UploadDocumentInputObjectSchema.superRefine(refineUploadFile);

export type UploadDocumentInput = z.infer<typeof UploadDocumentInputSchema>;

/**
 * One file within a multi-file upload. Each file becomes its own Document (and
 * its own inbox entry) — the batch only exists so that exactly one notification
 * is sent. Category is per-file so the customer can steer them individually; a
 * shared category is just the same value repeated.
 */
export const UploadFileSchema = z.object({
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    contentBase64: z.string().min(1),
    category: CategorySchema,
});
export type UploadFileInput = z.infer<typeof UploadFileSchema>;

/**
 * Boundary input for `uploadDocuments` (the multi-file upload Magellan call).
 * Caps the count (5) and enforces the per-file size (25 MB) and type
 * (PDF/JPEG/PNG/HEIC) allow-list via the shared {@link checkUploadFile} so the
 * client (UX feedback) and the server (enforcement) reject identically. The
 * optional `notificationText` is the customer's note bundled into the single
 * notification mail. `draft` mirrors the single upload (trustee four-eyes).
 *
 * `templateId` is the Nachrichtenvorlage the trustee picked for this Vorgang —
 * one per action, not per file, because the batch sends exactly one mail. Absent
 * means «take the Vorauswahl of the occasion», which is what an untouched dialog
 * submits; a customer upload never carries it.
 */
export const UploadDocumentsInputSchema = z
    .object({
        mandateId: z.string().min(1).transform(MandateId),
        files: z.array(UploadFileSchema).min(1).max(MAX_UPLOAD_FILE_COUNT),
        notificationText: z.string().min(1).optional(),
        templateId: z.string().min(1).optional(),
        draft: z.boolean().optional().default(false),
        // Idempotency key: the client mints one per upload attempt and reuses it
        // on retry. A replay with the same token returns the batch's already
        // created Documents instead of inserting duplicates (Testbericht T4).
        batchToken: z.string().min(1).optional(),
    })
    .superRefine((value, ctx) => {
        value.files.forEach((file, index) => {
            const rejection = checkUploadFile({
                fileName: file.fileName,
                contentType: file.contentType,
                sizeBytes: base64ByteLength(file.contentBase64),
            });
            if (rejection) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["files", index],
                    message:
                        rejection.reason === "size"
                            ? `Datei «${file.fileName}» überschreitet die maximale Dateigrösse von ${MAX_UPLOAD_FILE_SIZE_LABEL}.`
                            : `Datei «${file.fileName}» hat einen nicht erlaubten Typ. Erlaubt sind ${ALLOWED_UPLOAD_TYPES_LABEL}.`,
                });
            }
        });
        // Total-batch constraint: even five within-limit files must not exceed the
        // batch maximum the body limit is sized against.
        if (
            checkUploadBatch(
                value.files.map(file => ({ sizeBytes: base64ByteLength(file.contentBase64) }))
            ) === "batchSize"
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["files"],
                message: `Die Dokumente überschreiten zusammen die maximale Gesamtgrösse von ${MAX_UPLOAD_BATCH_SIZE_LABEL} pro Vorgang.`,
            });
        }
    });
export type UploadDocumentsInput = z.infer<typeof UploadDocumentsInputSchema>;

/**
 * Boundary input for `acceptDocument` (the review accept call). `templateId` is
 * the Nachrichtenvorlage picked in the release dialog: the four-eyes accept is
 * where the customer mail is actually triggered, so the choice belongs to whoever
 * releases, not to whoever uploaded the draft. Absent means «take the Vorauswahl».
 */
export const AcceptDocumentInputSchema = z.object({
    documentId: z.string().min(1).transform(DocumentId),
    templateId: z.string().min(1).optional(),
});
export type AcceptDocumentInput = z.infer<typeof AcceptDocumentInputSchema>;

/** Boundary input for `acceptDocuments` (the bulk review release). */
export const AcceptDocumentsInputSchema = z.object({
    documentIds: z.array(z.string().min(1).transform(DocumentId)).min(1),
    /** One picked Nachrichtenvorlage for the whole release — see {@link AcceptDocumentInputSchema}. */
    templateId: z.string().min(1).optional(),
});
export type AcceptDocumentsInput = z.infer<typeof AcceptDocumentsInputSchema>;

/**
 * Result of `retrieveDocument`: the file itself (base64-encoded bytes plus its
 * content type) alongside the (now retrieved) Document. The bytes travel base64
 * over Magellan, the same encoding uploads use inbound.
 */
export interface RetrievedDocument {
    document: Document;
    contentBase64: string;
    contentType: string;
}

/**
 * Result of `downloadDocumentsAsZip`: several Documents bundled into one ZIP
 * archive (base64-encoded bytes plus a suggested archive file name). Like
 * {@link RetrievedDocument} the bytes travel base64 over Magellan. Retrieving the
 * bundle flips each contained Document to `retrieved`, exactly as the single
 * retrieve does — the bulk form does not change the domain effect.
 */
export interface RetrievedArchive {
    contentBase64: string;
    contentType: string;
    fileName: string;
}
